/** Pointer play: `body.cursor` — a seat's cursor, placed on the pointer's
 * world point when the seat aims with a pointer and steered by the keys
 * otherwise — and `rule.pressable`, a pointer target: a fresh press of A with
 * a cursor over it signals `pressed` (carrying the placed item it stands
 * for), a held press signals `held`. Every tap/click interaction of a
 * composed game is these two modules; what a press MEANS is the listener's. */
import { bool, id, int, num, obj } from '../schemaBuilders.js';
import { defineModule, num as read, str } from '../module.js';
import type { EntityV1 } from '../world.js';

export type CursorConfig = { speed: number; pressTicks: number };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The point a cursor presses with: its centre (bottom-anchored entity). */
export function cursorPoint(cursor: EntityV1): { x: number; y: number } {
  return { x: cursor.x, y: cursor.y - cursor.h / 2 };
}

export const cursor = defineModule<CursorConfig>({
  id: 'body.cursor', version: 1, attach: 'entity', role: 'movement',
  summary: 'A seat cursor: jumps to the pointer point when the seat aims with a pointer, else glides by the keys; stays in the world; optional press pose.',
  config: obj({ speed: num(1, 60), pressTicks: int(0, 30) }),
  requires: [], events: [], presentation: {},
  proofs: ['follows-pointer-point', 'keys-steer-without-pointer', 'stays-in-world', 'press-pose'],
  animStates: c => (c.pressTicks > 0 ? ['action-1'] : []),
  init(ctx, e) { ctx.data(e).pose = 0; },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active' || e.slot === null) return;
      const c = ctx.config, d = ctx.data(e), input = ctx.input(e.slot), { w, h } = ctx.world.bounds;
      const at = cursorPoint(e);
      const x = input.p ? input.p.x : at.x + input.dx * c.speed * ctx.dt;
      const y = input.p ? input.p.y : at.y + input.dy * c.speed * ctx.dt;
      e.x = clamp(x, 0, w);
      e.y = clamp(y, 0, h) + e.h / 2;
      if (input.a && !input.prevA) d.pose = c.pressTicks;
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      if (read(d, 'pose') <= 0) return;
      ctx.setAnim(e, 'action-1', read(d, 'pose') === ctx.config.pressTicks);
      d.pose = read(d, 'pose') - 1;
    },
  },
});

export type PressableConfig = {
  /** Tag of the cursors that may press it. */
  cursorTag: string;
  /** Extra hit margin around the box, in world units (fingertips). */
  pad: number;
  /** Show a press pose (`action-1`). */
  pose: boolean;
};

/** True when the cursor's press point is over `e` (box grown by `pad`). */
function under(e: EntityV1, cursorEntity: EntityV1, pad: number): boolean {
  const p = cursorPoint(cursorEntity);
  return Math.abs(p.x - e.x) <= e.w / 2 + pad && p.y >= e.y - e.h - pad && p.y <= e.y + pad;
}

export const pressable = defineModule<PressableConfig>({
  id: 'rule.pressable', version: 1, attach: 'entity', role: 'rule',
  summary: 'A pointer target: a seat cursor freshly pressing A over it signals pressed (with its placed item), holding signals held; optional press pose.',
  config: obj({ cursorTag: id(), pad: num(0, 2), pose: bool() }),
  requires: ['body.cursor'], events: ['press'],
  presentation: { press: { cue: 'click', vfx: { effect: 'burst', palette: 'slot', count: 4 } } },
  proofs: ['fresh-press-over-signals', 'press-elsewhere-ignored', 'held-press-not-repeated', 'held-signals-held', 'pad-widens-target', 'carries-item'],
  animStates: c => (c.pose ? ['action-1'] : []),
  init(ctx, e, params) {
    Object.assign(ctx.data(e), { item: typeof params.item === 'string' ? params.item : e.archetype, pose: 0 });
  },
  systems: {
    // Interact: after every cursor moved this tick, before the rules that
    // listen for presses.
    interact(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      for (const presser of ctx.entities({ tag: c.cursorTag, status: 'active' })) {
        if (presser.slot === null) continue;
        const input = ctx.input(presser.slot);
        if (!input.a || !under(e, presser, c.pad)) continue;
        const data = { slot: presser.slot, item: str(d, 'item')!, x: e.x, y: e.y - e.h / 2 };
        if (input.prevA) { ctx.signal('held', e, presser, data); continue; }
        ctx.signal('pressed', e, presser, data);
        ctx.emit('press', data);
        d.pose = 6;
      }
    },
    present(ctx, e) {
      if (!e || !ctx.config.pose) return;
      const d = ctx.data(e);
      if (read(d, 'pose') <= 0) return;
      ctx.setAnim(e, 'action-1', read(d, 'pose') === 6);
      d.pose = read(d, 'pose') - 1;
    },
  },
});

/** The item a pressable entity stands for (null when it is not one). */
export function pressableItem(ctx: { peer(e: EntityV1, id: string): Record<string, unknown> | undefined }, e: EntityV1): string | null {
  const item = ctx.peer(e, 'rule.pressable')?.item;
  return typeof item === 'string' ? item : null;
}
